import { MutableRefObject, useEffect, useMemo } from 'react'
import { ListProps as ListComponentProps } from 'react-virtualized/dist/commonjs/List'
import _throttle from 'lodash/throttle'
import { CONFIG_EMPTY, ListVirtualizedProps } from '../index'
import { useBreakpoint, useDimension } from 'utils/Hooks'
import List from './List'
import ListWithScroll from './ListWithScroll'

export type SimpleListProps<Item> = Pick<ListComponentProps, 'className' | 'noRowsRenderer'> &
  Pick<
    ListVirtualizedProps<Item>,
    | 'calculateCellHeight'
    | 'classes'
    | 'config'
    | 'data'
    | 'loading'
    | 'loadingPosition'
    | 'loadingSize'
    | 'onScroll'
    | 'renderItem'
    | 'scrollRef'
  > & {
    debug?: boolean
    onEndOfGrid?: () => void
  }

function SimpleList<Item>(props: SimpleListProps<Item>) {
  const {
    className,
    config: configCustom,
    debug,
    data,
    loading,
    scrollRef,

    calculateCellHeight,
    noRowsRenderer,
    onEndOfGrid,
    onScroll,
    renderItem
  } = props
  const { breakpoint } = useBreakpoint()
  const [containerRefCallback, containerDimension, containerRef, refreshDimension] = useDimension({
    liveMeasure: true
  })
  const { width } = containerDimension

  const useWindow = scrollRef === window ? true : false
  const autoHeightAdjusted = scrollRef ? true : false
  const listContainerHeight = containerRef?.current?.clientHeight ?? 0
  const scrollContainerHeight = useWindow
    ? window.innerHeight
    : (scrollRef as MutableRefObject<any>)?.current?.clientHeight ?? 0

  const config = useMemo(
    () => ({
      ...(configCustom ? configCustom.default : CONFIG_EMPTY),
      ...(configCustom && breakpoint ? configCustom[breakpoint] : CONFIG_EMPTY)
    }),
    [breakpoint, configCustom]
  )
  const onBottomThrottle = useMemo(() => {
    if (!onEndOfGrid || !scrollRef) return undefined

    return _throttle(() => {
      onEndOfGrid()
    }, 800)
  }, [onEndOfGrid, scrollRef])

  // Added to solve load data when grid height smaller than parent height.
  // No need to scroll to load new data
  useEffect(() => {
    if (
      autoHeightAdjusted &&
      onEndOfGrid &&
      !loading &&
      listContainerHeight &&
      scrollContainerHeight &&
      listContainerHeight <= scrollContainerHeight
    ) {
      onEndOfGrid()
    }
  }, [autoHeightAdjusted, loading, listContainerHeight, scrollContainerHeight, onEndOfGrid])

  const isWidthDifferent = containerRef?.current?.clientWidth !== width

  useEffect(() => {
    if (isWidthDifferent) refreshDimension()
  }, [isWidthDifferent, refreshDimension])

  return (
    <div ref={containerRefCallback} className={className}>
      {scrollRef && onEndOfGrid ? (
        <ListWithScroll
          calculateCellHeight={calculateCellHeight}
          containerRef={containerRef}
          debug={debug}
          config={config}
          data={data}
          noRowsRenderer={noRowsRenderer}
          onBottom={onBottomThrottle}
          onScroll={onScroll}
          renderItem={renderItem}
          scrollRef={scrollRef}
          width={width}
        />
      ) : (
        <List
          calculateCellHeight={calculateCellHeight}
          debug={debug}
          config={config}
          data={data}
          noRowsRenderer={noRowsRenderer}
          renderItem={renderItem}
          width={width}
        />
      )}
    </div>
  )
}

export default SimpleList
